<template>
  <loader v-if="loading"/>
  <div v-else class="create-credentials-flow">
    <source-type-selection-modal v-if="componentName === componentNames.sourceTypeSelectionModal"
                                 :close-action="closeAction"
                                 @source-type-selected="handleSourceTypeSelected"/>
    <source-selection-modal v-else-if="componentName === componentNames.sourceSelectionModal"
                            :source-type="sourceType || knownSourceType" :close-action="closeAction" :set-selected-source="setSelectedSource"
                            :back-action="backToSourceTypeSelection"/>
    <connection-flow-modal v-else-if="componentName === componentNames.connectionFlowModal" v-bind="connectionFlowModalProps"/>
    <credentials-connection-success v-else-if="componentName === componentNames.credentialsConnectionSuccess"
                                    :source-name="sourceToConnect" :source-type="sourceType" :close-action="closeAfterSuccess"/>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { getSourceConfigurationBySourceName } from '@/constants/sourcesConfiguration';
import SourceSelectionModal
  from '@/pages/responsive-pages/authenticated/onboard2/steps/create-credentials-flow/SourceSelectionModal';
import ConnectionFlowModal
  from '@/pages/responsive-pages/authenticated/onboard2/steps/create-credentials-flow/ConnectionFlowModal';
import CredentialsConnectionSuccess
  from '@/pages/responsive-pages/authenticated/onboard2/steps/create-credentials-flow/CredentialsConnectionSuccess';
import LoadingPopup from '@/base-components/onboarding/LoadingPopup';
import _ from 'lodash';
import SourceTypeSelectionModal from './SourceTypeSelectionModal';

const componentNames = {
  sourceTypeSelectionModal: 'source-type-selection-modal',
  sourceSelectionModal: 'source-selection-modal',
  connectionFlowModal: 'connection-flow-modal',
  credentialsConnectionSuccess: 'credentials-connection-success',
};

export default {
  name: 'AddCredentialsFlow',
  data() {
    return {
      componentName: null,
      loading: false,
      sourceType: null,
      sourceToConnect: null,
      accountInvestigationFinished: false,
      nameAlreadyInUseError: false,
    };
  },
  components: {
    SourceTypeSelectionModal,
    SourceSelectionModal,
    ConnectionFlowModal,
    CredentialsConnectionSuccess,
  },
  props: {
    allowBackToSourceSelection: {
      type: Boolean,
      default: true,
    },
    sourceNameToAdd: {
      type: String,
      required: false,
    },
    sourceToUpdate: {
      type: Object,
      required: false,
    },
    updateMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    knownSourceType: {
      type: String,
      required: false,
    },
  },
  async created() {
    if (this.sourceName) {
      this.sourceToConnect = this.sourceName;
      this.sourceType = getSourceConfigurationBySourceName(this.sourceName).type;
      this.componentName = this.componentNames.connectionFlowModal;
    } else if (this.knownSourceType) {
      this.componentName = this.componentNames.sourceSelectionModal;
    } else {
      this.componentName = this.componentNames.sourceTypeSelectionModal;
    }

    if (this.sourceToUpdate && this.sourceToUpdate.type) {
      this.sourceType = this.sourceToUpdate.type;
    }
  },
  computed: {
    ...mapState('credentials', ['credentialsValid', 'credentialsList', 'credentialsSaveError']),
    ...mapState('credentials', ['isLoading']),
    componentNames() {
      return componentNames;
    },
    componentsOrder() {
      return [
        componentNames.sourceTypeSelectionModal,
        componentNames.sourceSelectionModal,
        componentNames.connectionFlowModal,
        componentNames.credentialsConnectionSuccess,
      ];
    },
    sourceName() {
      return this.sourceNameToAdd || this.sourceToUpdate?.sourceName;
    },
    connectionFlowModalProps() {
      const propsToPass = {
        sourceType: this.sourceType,
        closeAction: this.closeAction,
        invalidCredentials: !this.credentialsValid,
        nameAlreadyUsedError: false,
        setNameAlreadyInUseError: nameAlreadyUsedError => { this.nameAlreadyInUseError = nameAlreadyUsedError; },
        validateName: this.validateName,
        saveCredentialsError: this.credentialsSaveError,
        next: this.goToNextComponent,
        sourceName: this.sourceToConnect,
        isLoading: this.isLoading,
        updateMode: this.updateMode,
        backAction: this.closeAction,
        onConnectionSuccess: this.handleConnectionSuccess,
        onSave: this.saveCredentials,
        asyncTimeoutReached: false,
        saveDiscountDirect: () => {},
      };

      if (!this.sourceName) {
        propsToPass.backAction = this.backToSourceSelection;
      }
      return propsToPass;
    },
    sourceTypeString() {
      return (this.sourceType === 'bank') ? 'הבנק' : 'חברת האשראי';
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    ...mapActions('credentials', ['saveCredentialsNew', 'fetchCredentials', 'updateCredentials']),
    async saveCredentials({ sourceName, credentials, name }) {
      if (this.updateMode) {
        await this.updateCredentials({ source: this.sourceToUpdate, credentials, renew: true });
      } else {
        await this.saveCredentialsNew({ sourceName, credentials, name });
      }
    },
    async handleConnectionSuccess() {
      await this.fetchCredentials();
    },
    validateName({ sourceName, name }) {
      const existingCreds = _.find(this.credentialsList, { sourceName, name });
      this.nameAlreadyInUseError = !!existingCreds;
    },
    handleSourceTypeSelected(sourceType) {
      this.sourceType = sourceType;
      this.goToNextComponent();
    },
    async closeAction() {
      this.loading = true;
      this.sourceToConnect = null;
      this.componentName = null;
      this.$emit('close');
      this.loading = false;
    },
    goToNextComponent() {
      const currentComponentIndex = _.findIndex(this.componentsOrder, component => component === this.componentName);
      this.componentName = this.componentsOrder[currentComponentIndex + 1];
    },
    async backToSourceTypeSelection() {
      this.componentName = this.componentNames.sourceTypeSelectionModal;
      this.sourceType = null;
    },
    async backToSourceSelection() {
      this.componentName = this.componentNames.sourceSelectionModal;
      this.sourceToConnect = null;
    },
    async setSelectedSource(source) {
      this.sourceToConnect = source.sourceName;
      this.goToNextComponent();
    },
    openLoadingPopup() {
      this.openModal({
        component: LoadingPopup,
        popupAlignment: 'center',
        props: {
          title: 'עוד רגע נמשיך',
          subtitle: `המערכת מתחברת לאתר ${this.sourceTypeString}`,
        },
        showLastModal: true,
      });
    },
    closeAfterSuccess() {
      this.$emit('success');
      this.closeAction();
    },
  },
  watch: {
    isLoading(newValue, oldValue) {
      if (newValue) {
        this.openLoadingPopup();
      } else if (oldValue && !newValue) {
        this.closeModal();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.create-credentials-flow {
  height: 100%;
}

</style>
